import { RecommendationKind } from "../App";
import { TBSRecommendation } from "../components/CSHPTBSCard";
import { QnARecommendation } from "../components/QnACard";
import { RecommendationList } from "../components/Recommendation";
import { QuestionSubject, Textbook } from "../components/Select";
import GraphQLHelper from "./GraphQLHelper";
import { params } from "../context";

export interface RecommendationConfig {
	baseUrl: string;
}

export interface RecentActivity {
	qnaSubject?: QuestionSubject[];
	qnaSubjectSelectedGroupId?: number;
	tbs?: Textbook[];
	tbsSelectedGroupId?: string;
	tbsSelectedIsbn13?: string;
}

const RecommendationService = {
	recentActivity: (): Promise<any> => {
		return GraphQLHelper.query(
			{
				"operationName": "recentActivity"
			},
			params.bff.retry.tbs_recommendations
		);
	},

	questions: (groupId: number, limit: number): Promise<any> => {
		return GraphQLHelper.query(
			{
				"operationName": "qna_recommendations",
				"variables": {
					"group_id": groupId,
					"maxRecsPerType": limit
				}
			},
			params.bff.retry.qna_recommendations
		);
	},

	problems: (groupId: number, isbn13: string, limit: number): Promise<any> => {
		return GraphQLHelper.query(
			{
				"operationName": "tbs_recommendations",
				"variables": {
					"isbn13": isbn13,
					"group_id": groupId,
					"maxRecsPerType": limit
				}
			},
			params.bff.retry.tbs_recommendations
		);
	},

	async fetchRecentActivity(): Promise<RecentActivity> {
		try {
			let response = await RecommendationService.recentActivity();
			response = response as {
				error?: any;
				data?: {
					recent_activity: RecentActivity;
				};
			};

			if (response.error) {
				return Promise.reject(response.error);
			}
			return Promise.resolve(response.data.recent_activity);
		} catch (e) {
			return Promise.reject(`Exception in fetching recent activity - ${e}`);
		}
	},

	fetchRecommendations: async (data: {
		kind: RecommendationKind;
		selected: string;
		limit: string;
	}): Promise<RecommendationList> => {
		let recommendationList: RecommendationList;
		let response;
		let errorMessage = "";
		try {
			if (data.kind === RecommendationKind.QnA) {
				response = (await RecommendationService.questions(
					parseInt(data.selected),
					parseInt(data.limit)
				)) as {
					error?: any;
					data?: {
						qna_recommendations: { id: number; items: QnARecommendation[] };
					};
				};
				if (response.error) throw response.error;
				const questionRecommendations = response.data.qna_recommendations;
				recommendationList = {
					kind: data.kind,
					set: questionRecommendations,
				};
			} else {
				const [isbn13, groupId] = data.selected.split(":");
				response = (await RecommendationService.problems(
					parseInt(groupId),
					isbn13,
					parseInt(data.limit)
				)) as {
					error?: any;
					data?: {
						tbs_recommendations: { id: number; items: TBSRecommendation[] };
					};
				};
				if (response.error) throw response.error;
				const problemRecommendations = response.data.tbs_recommendations;
				recommendationList = {
					kind: data.kind,
					set: problemRecommendations,
				};
			}
		} catch (e) {
			errorMessage = `Error in fetching ${
				data.kind === RecommendationKind.QnA ? "question" : "textbook"
			} recommendations`;
			return Promise.reject(errorMessage);
		}

		return Promise.resolve(recommendationList);
	},
};

export default RecommendationService;
